import { useState } from 'react';
import Card from './Card';
import { WIDGETS, ROLES, DEFAULT_VISIBILITY } from '../data/widgets';
import {
  RotateCcw,
  ClipboardList,
  BarChart3,
  Filter,
  Gauge,
  AlertTriangle,
  Activity,
  CreditCard,
  CheckCircle2,
  ShieldCheck,
} from 'lucide-react';

const WIDGET_ICONS = {
  attention: ClipboardList,
  kpis: BarChart3,
  funnel: Filter,
  riskMix: Gauge,
  alertBanner: AlertTriangle,
  activity: Activity,
  usageBar: CreditCard,
};

export default function WidgetPermissions({ visibility, onChange }) {
  const [selectedRole, setSelectedRole] = useState(ROLES[0].id);
  const [saved, setSaved] = useState(false);

  const visibleIds = visibility[selectedRole] || [];

  const flashSaved = () => {
    setSaved(true);
    setTimeout(() => setSaved(false), 1400);
  };

  const toggleWidget = (widgetId) => {
    const next = visibleIds.includes(widgetId)
      ? visibleIds.filter((id) => id !== widgetId)
      : [...visibleIds, widgetId];
    onChange({ ...visibility, [selectedRole]: next });
    flashSaved();
  };

  const resetRole = () => {
    onChange({
      ...visibility,
      [selectedRole]:
        DEFAULT_VISIBILITY[selectedRole] || WIDGETS.map((w) => w.id),
    });
    flashSaved();
  };

  return (
    <Card delay={60} className="p-5 md:p-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 pb-5 border-b border-slate-100">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center">
            <ShieldCheck className="w-5 h-5 text-brand" strokeWidth={1.8} />
          </div>
          <div>
            <h2 className="font-semibold text-slate-900">
              Widget access by role
            </h2>
            <p className="text-xs text-slate-400 mt-0.5">
              Control which dashboard widgets each role can see. Changes apply
              instantly.
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <span
            className={`inline-flex items-center gap-1 text-xs font-medium text-[#16A34A] transition-all duration-300 ${
              saved ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-1'
            }`}
          >
            <CheckCircle2 size={14} /> Saved
          </span>
          <button
            onClick={resetRole}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg border border-slate-300 text-xs font-medium text-slate-700 hover:border-brand hover:text-brand transition-all duration-200 active:scale-95"
          >
            <RotateCcw size={13} />
            Reset role defaults
          </button>
        </div>
      </div>

      <div className="grid lg:grid-cols-[240px_1fr] gap-6 mt-6">
        {/* Role picker */}
        <div className="flex lg:flex-col gap-2 overflow-x-auto pb-2 lg:pb-0">
          {ROLES.map((role) => {
            const count = (visibility[role.id] || []).length;
            const active = selectedRole === role.id;
            return (
              <button
                key={role.id}
                onClick={() => setSelectedRole(role.id)}
                className={`shrink-0 lg:w-full flex items-center justify-between gap-3 rounded-xl px-4 py-3 text-sm font-medium transition-all duration-200 ${
                  active
                    ? 'bg-brand text-white shadow-lift'
                    : 'bg-slate-50 text-slate-700 hover:bg-slate-100'
                }`}
              >
                <span>{role.label}</span>
                <span
                  className={`text-[10px] font-bold rounded-full px-2 py-0.5 tabular-nums ${
                    active
                      ? 'bg-white/20 text-white'
                      : 'bg-slate-200 text-slate-600'
                  }`}
                >
                  {count}/{WIDGETS.length}
                </span>
              </button>
            );
          })}
        </div>

        {/* Widget toggles for selected role */}
        <div className="space-y-3">
          {WIDGETS.map((w, i) => {
            const Icon = WIDGET_ICONS[w.id] || ClipboardList;
            const on = visibleIds.includes(w.id);
            return (
              <div
                key={w.id}
                className={`animate-fade-up flex items-center justify-between gap-4 rounded-xl border p-4 transition-all duration-200 ${
                  on
                    ? 'border-brand/30 bg-brand/[0.03]'
                    : 'border-slate-200 bg-white'
                }`}
                style={{ animationDelay: `${i * 50}ms` }}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div
                    className={`w-9 h-9 rounded-lg flex items-center justify-center transition-colors duration-200 ${
                      on
                        ? 'bg-brand-soft text-brand'
                        : 'bg-slate-100 text-slate-400'
                    }`}
                  >
                    <Icon size={17} strokeWidth={1.8} />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-slate-900">
                      {w.label}
                    </p>
                    <p className="text-[11px] text-slate-400 truncate">
                      {w.description}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-3 shrink-0">
                  <span
                    className={`text-[10px] font-bold tracking-wider ${
                      on ? 'text-[#16A34A]' : 'text-slate-400'
                    }`}
                  >
                    {on ? 'VISIBLE' : 'HIDDEN'}
                  </span>
                  <button
                    onClick={() => toggleWidget(w.id)}
                    aria-label={`Toggle ${w.label}`}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition ${
                      on ? 'bg-brand' : 'bg-gray-300'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${
                        on ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </Card>
  );
}
